import { z } from 'zod'
import {
  type DecisionMail,
  EXCLUSIONS_GMAIL,
  JOURS_PREMIER_PASSAGE,
  mailVisibleDuProprietaire,
} from './boites'
import { ajouterJours, ecartJours, instantDeParis, jourAParis } from './calendrier'
import { motifExpediteur } from './consignes'
import {
  type CategorieSansPiece,
  type CoupureRecherche,
  DEVISE_PAR_DEFAUT,
  type IssueMailRecherche,
  PALIERS_FORTS,
  PALIERS_RECHERCHE,
  type PalierRecherche,
} from './domaine'
import type { Perimetre } from './droits'
import { type Cloison, cloisonDe } from './espaces'
import { normaliserLibelle } from './libelle'
import {
  type NatureOperation,
  type OperationBancaire,
  motsDuMarchand,
  referencesDuLibelle,
} from './operations-bancaires'
import { libelleDuTiersReconnu } from './rapprochement'
import { motGenerique } from './recuperation-rattachement'

// Recherche des pièces d'opérations bancaires dans les mails anciens (au-delà du domaine de la
// relève). Le port ci-dessous est l'entrée commune du service de recherche et de ceux qui
// l'appellent (rapprochement, validation des opérations du cabinet). Fonctions pures : fenêtre
// de recherche, termes tirés de l'opération, requêtes Gmail par paliers, du plus étroit au plus
// large, toujours sur une pièce jointe PDF ou XML, avec les exclusions de la relève et un terme
// distinctif. Rien du contenu d'un mail ici.

/**
 * Issue de la recherche d'une opération : 'trouvee' quand au moins une pièce, entrée ou déjà
 * connue, vient d'un mail de la fenêtre ; 'rien' quand toutes les requêtes sont passées sans
 * rien garder ; 'coupee' au plafond de mails ou d'IA, ou sur une erreur Gmail (à reprendre) ;
 * 'sans_boite' quand aucune boîte interrogeable ne couvre l'entité.
 */
export type IssueRechercheMails = 'trouvee' | 'rien' | 'coupee' | 'sans_boite'

export type ResultatRechercheMails = {
  issue: IssueRechercheMails
  pieceIds: readonly string[]
  mailsLus: number
  motif?: string
}

/** Cherche les pièces de chaque opération ; le résultat est indexé par la ref de l'opération. */
export type ChercheurPiecesMails = (d: {
  entiteId: string
  operations: readonly (OperationBancaire & { ref: string })[]
}) => Promise<ReadonlyMap<string, ResultatRechercheMails>>

/** La pièce d'une opération arrive au plus tôt quarante-cinq jours avant elle… */
export const JOURS_AVANT_OPERATION = 45
/** … et au plus tard dix jours après. */
export const JOURS_APRES_OPERATION = 10
/** Requêtes d'une recherche, au plus. */
export const REQUETES_MAX = 5
/** Au-delà, une requête Gmail n'est plus lue : trop large. */
export const LONGUEUR_MAX_REQUETE = 1500

// Domaine de la relève : le premier passage lit les trente derniers jours, l'historique tout ce
// qui arrive ensuite. La recherche lit ce qui précède, jamais ce que la relève a pu lire.

/** Premier jour (Paris) du domaine de la relève : aujourd'hui moins trente jours. */
export function borneDomaineReleve(maintenant: Date): string {
  return ajouterJours(jourAParis(maintenant), -JOURS_PREMIER_PASSAGE)
}

/** Ce mail a-t-il été reçu dans le domaine de la relève (à partir de sa borne, minuit à Paris) ? */
export function dansDomaineReleve(recuLe: Date, maintenant: Date): boolean {
  return jourAParis(recuLe) >= borneDomaineReleve(maintenant)
}

/**
 * Un mail reçu plus de trente jours (de Paris) avant son entrée dans l'appli vient d'une
 * recherche : la relève ne lit que les trente derniers jours. Il n'ouvre ni dossier de mail ni
 * tâche « Payer ». Sans date de réception : non.
 */
export function mailDeRecherche(recuLe: Date | null, entreLe: Date): boolean {
  if (!recuLe) return false
  return ecartJours(jourAParis(recuLe), jourAParis(entreLe)) > JOURS_PREMIER_PASSAGE
}

/** Jours de Paris, bornes comprises. */
export type FenetreRecherche = { du: string; au: string }

/**
 * Fenêtre de recherche : autour de la date d'une opération, ou la période d'une cible
 * périodique, coupée la veille de la borne de la relève. Null si elle est vide.
 */
export function fenetreRecherche(
  cible: { date: string } | FenetreRecherche,
  maintenant: Date,
): FenetreRecherche | null {
  const [du, au] =
    'date' in cible
      ? [
          ajouterJours(cible.date, -JOURS_AVANT_OPERATION),
          ajouterJours(cible.date, JOURS_APRES_OPERATION),
        ]
      : [cible.du, cible.au]
  const dernier = ajouterJours(borneDomaineReleve(maintenant), -1)
  const fin = au < dernier ? au : dernier
  return du <= fin ? { du, au: fin } : null
}

/**
 * Bornes Gmail en secondes depuis 1970 : minuit à Paris du premier jour, et du lendemain du
 * dernier (before est exclusif). Jamais AAAA/MM/JJ, que Gmail lit à l'heure du Pacifique.
 */
export function bornesGmail(f: FenetreRecherche): { apres: number; avant: number } {
  const minuit = (jour: string) => Date.parse(instantDeParis(jour, '00:00')) / 1000
  return { apres: minuit(f.du), avant: minuit(ajouterJours(f.au, 1)) }
}

/**
 * Formes d'un montant dans un mail, en valeur absolue : française groupée et simple, anglaise
 * groupée et simple, sans doublon. Espace ordinaire, ni espace fine ni symbole (Gmail cherche
 * des mots). Montant nul : rien.
 */
export function montantsPourRecherche(montantCts: number): string[] {
  if (!Number.isSafeInteger(montantCts) || montantCts === 0) return []
  const absolu = Math.abs(montantCts)
  const euros = String(Math.floor(absolu / 100))
  const cents = String(absolu % 100).padStart(2, '0')
  const grouper = (sep: string) => euros.replace(/\B(?=(\d{3})+(?!\d))/g, sep)
  return [
    ...new Set([
      `${grouper(' ')},${cents}`,
      `${euros},${cents}`,
      `${grouper(',')}.${cents}`,
      `${euros}.${cents}`,
    ]),
  ]
}

/** Termes d'une recherche, déjà sûrs pour une requête Gmail. */
export type TermesRecherche = {
  /** Adresses ou domaines d'expéditeur (from:). */
  adresses: readonly string[]
  /** Mots qui nomment le tiers. */
  mots: readonly string[]
  /** Références (numéro de facture, de client). */
  references: readonly string[]
  /** Formes du montant. */
  montants: readonly string[]
  /**
   * Mots du nom du fournisseur que Claude a lu dans le libellé (`motsDuNomFournisseur`) :
   * cherchés tous ensemble, toujours avec le montant, jamais seuls.
   */
  noms?: readonly string[]
}

/** Adresse complète ou domaine, en caractères sûrs pour from:. */
const ADRESSE = /^([a-z0-9._%+-]+@)?([a-z0-9-]+\.)+[a-z]{2,}$/
/** Un terme entre guillemets : ni espace, ni guillemet, ni accolade, ni parenthèse, ni deux-points. */
const TERME = /^[\p{L}\p{N}][\p{L}\p{N}.'&@_-]{0,79}$/u
/** Une référence cherchée en texte : six signes au moins, dont un chiffre. */
const REFERENCE = /^(?=.*\d)[A-Z0-9][A-Z0-9._-]{5,39}$/
/** Montant écrit (« 1 270,00 », « 1,270.00 ») : jamais un critère à lui seul. */
const MONTANT_ECRIT = /^\d{1,3}(?:[ ,]\d{3})*[,.]\d{2}$|^\d+[,.]\d{2}$/
const MOTS_PAR_RECHERCHE = 3
const REFERENCES_PAR_RECHERCHE = 3
const ADRESSES_PAR_RECHERCHE = 10

/**
 * Mots d'un libellé qui ne nomment pas le tiers pour une recherche, absents de la liste de
 * motsDuMarchand : liaison (« vers »), mandat, créancier ou prêt, et mots de forme des libellés
 * (chèque, remise, espèces, « en votre faveur », « /MOTIF », canal, nature du document). Seul
 * ou avec un montant, un tel mot trouverait n'importe quel mail.
 */
const MOTS_SANS_TIERS = new Set([
  'VERS',
  'CHEZ',
  'RUM',
  'ICS',
  'MANDAT',
  'PRET',
  'EMPRUNT',
  'NOTPROVIDED',
  'CHEQUE',
  'CHEQUES',
  'CHQ',
  'REMISE',
  'VERSEMENT',
  'ESPECES',
  'FAVEUR',
  'EUROPEEN',
  'MOTIF',
  'REFDO',
  'LIBELLE',
  'WEB',
  'PIECE',
  'NUMERO',
  'AVOIR',
])
/**
 * Préfixe d'une référence collée (« RUM:MA970001 », « ECH:011026 ») qui n'est jamais celle
 * d'une pièce : mandat, créancier, prêt, échéance ou date.
 */
const PREFIXES_SANS_PIECE = new Set([
  'RUM',
  'ICS',
  'IBAN',
  'BIC',
  'MANDAT',
  'PRET',
  'EMPRUNT',
  'ECH',
  'ECHEANCE',
  'DATE',
])
/**
 * Une période (« 06-2026 », « 2026-06 », « 202606 ») : Gmail la lit comme deux mots, ou la
 * trouve dans tout document du mois. Jamais une référence à elle seule.
 */
const PERIODE = /^(\d{1,2}[.-]\d{4}|\d{4}[.-]\d{1,2}|20\d{2}(0[1-9]|1[0-2]))$/

function adressesSures(adresses: readonly string[]): string[] {
  const res = new Set<string>()
  for (const a of adresses) {
    const n = a.trim().toLowerCase()
    if (ADRESSE.test(n)) res.add(n)
  }
  return [...res].slice(0, ADRESSES_PAR_RECHERCHE)
}

/**
 * Références du libellé, découpées aux deux-points (« EDF:5568268225 »), sans mandat, échéance
 * ni période.
 */
function referencesPourRecherche(libelle: string): string[] {
  const res = new Set<string>()
  for (const r of referencesDuLibelle(libelle)) {
    const parties = r.split(':').filter(Boolean)
    if (parties.length > 1 && PREFIXES_SANS_PIECE.has(parties[0]!)) continue
    for (const p of parties) if (REFERENCE.test(p) && !PERIODE.test(p)) res.add(p)
  }
  return [...res].slice(0, REFERENCES_PAR_RECHERCHE)
}

/**
 * Termes d'une opération : les mots du tiers (motsDuMarchand sans mot générique, sans ceux de
 * `exclus` : noms de l'entité et de ses titulaires, présents dans tous ses mails), les
 * références du libellé, les formes du montant et les adresses connues du tiers.
 */
export function termesDeOperation(
  op: Pick<OperationBancaire, 'libelle' | 'marchand' | 'montantCts'>,
  contexte: { adresses?: readonly string[]; exclus?: readonly string[] } = {},
): TermesRecherche {
  const exclus = new Set(
    (contexte.exclus ?? []).flatMap((t) => motsDuMarchand({ libelle: t, marchand: null })),
  )
  const mots = motsDuMarchand(op)
    .filter((m) => !motGenerique(m) && !MOTS_SANS_TIERS.has(m) && !exclus.has(m) && TERME.test(m))
    .slice(0, MOTS_PAR_RECHERCHE)
  return {
    adresses: adressesSures(contexte.adresses ?? []),
    mots,
    references: referencesPourRecherche(op.libelle),
    montants: montantsPourRecherche(op.montantCts),
  }
}

/**
 * Les adresses du tiers que le libellé bancaire reconnaît (`libelleDuTiersReconnu`, la règle du
 * rapprochement), s'il n'en reconnaît qu'un ; sinon aucune. L'appelant ne donne que des tiers
 * que voient les lecteurs de l'entité cible.
 */
export function adressesDuTiersReconnu(
  tiers: readonly { libelles: readonly string[]; adresses: readonly string[] }[],
  libelle: string,
): string[] {
  const l = normaliserLibelle(libelle)
  const reconnus = tiers.filter((t) => libelleDuTiersReconnu(t.libelles, l))
  return reconnus.length === 1 ? [...reconnus[0]!.adresses] : []
}

// Nom du fournisseur lu par Claude (décision du 14/10/2026) : un libellé qui nomme un site
// (« NUAGE.IO SUBSCRIPTION ») et non la société qui facture (« Nuagerie ») ne trouve rien
// quand aucune fiche ne donne l'expéditeur. Claude reçoit les seuls mots du marchand, sans
// outil, et rend un nom ou rien ; ce nom ne sert qu'à chercher, avec le montant, et ne s'écrit
// jamais dans une fiche.

/** Longueur d'un nom de fournisseur rendu par Claude, au plus : au-delà, il est ignoré. */
export const NOM_FOURNISSEUR_MAX = 60
/** Mots du nom cherchés, au plus. */
const MOTS_DU_NOM = 2
/**
 * Réponse de Claude, nom ou rien, gardée au journal de l'entité : la question n'est posée qu'une
 * fois par marchand et par entité.
 */
export const ACTION_NOM_FOURNISSEUR = 'nommer_fournisseur'
/** Coût estimé d'une question (petit modèle, quelques centaines de jetons), en micro-USD. */
export const NOM_FOURNISSEUR_ESTIME_MICRO_USD = 2_000

/** Empreinte de la clé d'un marchand, seule gardée au journal (ni libellé ni montant). */
export function empreinteNomFournisseur(cle: string): string {
  return empreinteCourte(cle)
}

/**
 * Ce que Claude lit d'une opération pour nommer son fournisseur, et la clé de son cache : les
 * mots du marchand sans chiffres (ni carte, ni date, ni référence), dans l'ordre. Deux débits du
 * même site partagent la clé. Null : rien qui nomme un marchand.
 */
export function cleNomFournisseur(op: Pick<OperationBancaire, 'libelle' | 'marchand'>) {
  const mots = motsDuMarchand(op).filter(
    (m) => !/\d/.test(m) && !motGenerique(m) && !MOTS_SANS_TIERS.has(m) && TERME.test(m),
  )
  return mots.length > 0 ? mots.slice(0, 4).join(' ') : null
}

/**
 * Le nom du fournisseur se demande pour une opération dont aucune fiche ne donne l'expéditeur,
 * qui a un montant (le nom ne se cherche jamais seul) et un marchand à nommer.
 */
export function nomFournisseurADemander(
  genre: 'operation' | 'periodique',
  t: TermesRecherche,
  cle: string | null,
): boolean {
  return genre === 'operation' && cle !== null && t.adresses.length === 0 && t.montants.length > 0
}

/**
 * Mots cherchés du nom rendu par Claude : ses mots de marchand (sans forme juridique, moyen de
 * paiement ni référence), ni génériques ni de forme, deux au plus. Vide quand le nom manque, est
 * trop long, ou n'apporte rien que le libellé ne cherche déjà (`dejaCherches`).
 */
export function motsDuNomFournisseur(
  nom: string | null,
  dejaCherches: readonly string[],
): string[] {
  if (!nom || nom.length > NOM_FOURNISSEUR_MAX) return []
  const mots = motsDuMarchand({ libelle: nom.replace(/[^\p{L}\p{N}]+/gu, ' '), marchand: null })
    .filter(
      (m) =>
        m.length >= 3 &&
        !/^\d+$/.test(m) &&
        !motGenerique(m) &&
        !MOTS_SANS_TIERS.has(m) &&
        TERME.test(m),
    )
    .slice(0, MOTS_DU_NOM)
  const deja = new Set(dejaCherches.map((m) => normaliserLibelle(m)))
  return mots.every((m) => deja.has(m)) ? [] : mots
}

/** Termes d'une cible périodique : ses expéditeurs et ses mots, tels qu'un humain les a donnés. */
export function termesDeCible(c: {
  expediteurs: readonly string[]
  mots: readonly string[]
}): TermesRecherche {
  return {
    adresses: adressesSures(c.expediteurs),
    mots: [...new Set(c.mots.map((m) => m.trim()).filter((m) => TERME.test(m)))],
    references: [],
    montants: [],
  }
}

export type RequeteRecherche = { palier: PalierRecherche; q: string }

/** Pièce jointe PDF ou XML exigée par une requête (sauf mail qui vaut pièce). */
const PIECE_JOINTE = ['has:attachment', '{filename:pdf filename:xml}'] as const

/**
 * Le motif cherché (adresse ou domaine) est-il couvert par celui d'une consigne « le mail vaut
 * pièce » : la même adresse, ou une adresse ou un sous-domaine du domaine de la consigne.
 */
function motifCouvert(cherche: string, couverture: string): boolean {
  const c = cherche.toLowerCase()
  const m = couverture.toLowerCase()
  if (m.includes('@')) return c === m
  const domaine = c.includes('@') ? c.slice(c.lastIndexOf('@') + 1) : c
  return domaine === m || domaine.endsWith(`.${m}`)
}

/**
 * Tous les expéditeurs cherchés envoient des mails qui valent pièce (motifs des consignes
 * actives qui le disent) : leurs requêtes se passent de pièce jointe.
 */
export function expediteursQuiValentPiece(
  adresses: readonly string[],
  couverture: readonly string[],
): boolean {
  return adresses.length > 0 && adresses.every((a) => couverture.some((m) => motifCouvert(a, m)))
}

const guillemets = (t: string) => `"${t}"`
/** Accolades : l'un ou l'autre ; un seul élément s'écrit nu. */
const groupe = (elements: readonly string[]) =>
  elements.length === 1 ? elements[0]! : `{${elements.join(' ')}}`
/** Un nom de domaine (« NUAGE.IO »), pas un mot qui contient un point (« 450.00 »). */
const DOMAINE_MOT = /^([\p{L}\p{N}-]+\.)+\p{L}{2,}$/u
const domaine = (mot: string) => DOMAINE_MOT.test(mot)

/**
 * Mots cherchés seuls. Opération (mots du libellé) : un domaine, seul ; sinon tous les mots
 * ensemble, à condition que l'un fasse cinq signes au moins. Cible périodique (mots donnés par
 * un humain) : l'un ou l'autre, comme avec un expéditeur, chacun de cinq signes au moins (ou un
 * domaine), jamais un montant. Null : trop large.
 */
function motsSeuls(genre: 'operation' | 'periodique', mots: readonly string[]): string | null {
  if (genre === 'periodique') {
    const surs = mots.filter((m) => (m.length >= 5 || domaine(m)) && !MONTANT_ECRIT.test(m))
    return surs.length ? groupe(surs.map(guillemets)) : null
  }
  const d = mots.find(domaine)
  if (d) return guillemets(d)
  if (!mots.some((m) => m.length >= 5)) return null
  return mots.map(guillemets).join(' ')
}

/**
 * Requêtes Gmail d'une recherche, dans l'ordre des paliers (PALIERS_RECHERCHE), chacun s'il a
 * ses ingrédients : expéditeur et montant, référence, expéditeur et mots (cible périodique),
 * expéditeur, mots et montant, mots seuls. Toutes sur une pièce jointe PDF ou XML, bornées à
 * la fenêtre, avec les exclusions de la relève ; jamais un montant seul. Exception : quand tous
 * ses expéditeurs sont couverts par une consigne « le mail vaut pièce » (`mailsQuiValentPiece`,
 * leurs motifs), un palier qui exige l'expéditeur se passe de pièce jointe. Sans doublon,
 * REQUETES_MAX au plus, chacune vérifiée par requeteSure. Vide : aucun critère sûr.
 */
export function requetesRecherche(
  genre: 'operation' | 'periodique',
  t: TermesRecherche,
  fenetre: FenetreRecherche,
  options: { mailsQuiValentPiece?: readonly string[] } = {},
): RequeteRecherche[] {
  const { apres, avant } = bornesGmail(fenetre)
  const couverture = options.mailsQuiValentPiece ?? []
  const fenetreEtExclusions = [`after:${apres}`, `before:${avant}`, ...EXCLUSIONS_GMAIL].join(' ')
  const base = [...PIECE_JOINTE, fenetreEtExclusions].join(' ')
  const sansPieceJointe = expediteursQuiValentPiece(t.adresses, couverture)
  const avecExpediteur = new Set<PalierRecherche>([
    'expediteur_montant',
    'expediteur_mots',
    'expediteur',
  ])
  const de = t.adresses.length ? groupe(t.adresses.map((a) => `from:${a}`)) : null
  const montant = t.montants.length ? groupe(t.montants.map(guillemets)) : null
  const mots = t.mots.length ? groupe(t.mots.map(guillemets)) : null
  const references = t.references.length ? groupe(t.references.map(guillemets)) : null
  // Tous les mots du nom, exigés ensemble.
  const noms = t.noms?.length ? t.noms.map(guillemets).join(' ') : null
  const criteres: Record<PalierRecherche, string | null> = {
    expediteur_montant: de && montant && `${de} ${montant}`,
    reference: references,
    expediteur_mots: genre === 'periodique' && de && mots ? `${de} ${mots}` : null,
    expediteur: de,
    mots_montant: mots && montant && `${mots} ${montant}`,
    nom_montant: genre === 'operation' && noms && montant ? `${noms} ${montant}` : null,
    mots: motsSeuls(genre, t.mots),
  }
  const vues = new Set<string>()
  const res: RequeteRecherche[] = []
  for (const palier of PALIERS_RECHERCHE) {
    const critere = criteres[palier]
    if (!critere) continue
    const q = `${critere} ${sansPieceJointe && avecExpediteur.has(palier) ? fenetreEtExclusions : base}`
    if (vues.has(q) || !requeteSure(q, couverture)) continue
    vues.add(q)
    res.push({ palier, q })
  }
  return res.slice(0, REQUETES_MAX)
}

/** Opérateurs permis hors guillemets ; les exclusions de la relève comprises. */
function jetonPermis(j: string): boolean {
  return (
    j === 'has:attachment' ||
    j === 'filename:pdf' ||
    j === 'filename:xml' ||
    /^(after|before):\d{1,12}$/.test(j) ||
    /^from:[a-z0-9._%+@-]+$/.test(j) ||
    EXCLUSIONS_GMAIL.includes(j)
  )
}

/**
 * Critères de premier niveau d'une requête (tous exigés) : chacun, la liste de ses alternatives
 * (l'une ou l'autre, entre accolades ; un seul élément hors accolades).
 */
function criteresDe(q: string): string[][] {
  const res: string[][] = []
  let alternatives: string[] | null = null
  for (const [j] of q.matchAll(/"[^"]*"|[{}]|[^\s{}"]+/g)) {
    if (j === '{') alternatives = []
    else if (j === '}') {
      if (alternatives) res.push(alternatives)
      alternatives = null
    } else if (alternatives) alternatives.push(j)
    else res.push([j])
  }
  return res
}

/** Un expéditeur, ou un terme entre guillemets qui n'est pas un montant. */
const distinctif = (j: string) =>
  j.startsWith('from:') || (j.startsWith('"') && !MONTANT_ECRIT.test(j.slice(1, -1)))

/**
 * Garde d'une requête de recherche : pièce jointe PDF ou XML, fenêtre bornée, toutes les
 * exclusions de la relève, et rien d'autre hors guillemets (ni in:, ni label:, ni OR, ni mot
 * nu) ; guillemets et accolades fermés ; un critère distinctif (expéditeur, ou terme entre
 * guillemets qui n'est pas un montant ; entre accolades, chacune de ses alternatives : un
 * montant en alternative suffirait seul) ; LONGUEUR_MAX_REQUETE au plus. Sans pièce jointe,
 * seulement des expéditeurs dont le mail vaut pièce (`couverture`), exigés au premier niveau.
 */
export function requeteSure(q: string, couverture: readonly string[] = []): boolean {
  if (q.length > LONGUEUR_MAX_REQUETE) return false
  const morceaux = q.split('"')
  if (morceaux.length % 2 === 0) return false
  const dehors = morceaux.filter((_, i) => i % 2 === 0).join(' ')
  const dedans = morceaux.filter((_, i) => i % 2 === 1)
  let profondeur = 0
  for (const c of dehors) {
    if (c === '{') profondeur++
    else if (c === '}') profondeur--
    if (profondeur < 0 || profondeur > 1) return false
  }
  if (profondeur !== 0) return false
  const jetons = dehors.replace(/[{}]/g, ' ').split(/\s+/).filter(Boolean)
  if (!jetons.every(jetonPermis)) return false
  if (!EXCLUSIONS_GMAIL.every((e) => jetons.includes(e))) return false
  const pieceJointe = ['has:attachment', 'filename:pdf', 'filename:xml']
  if (!pieceJointe.every((e) => jetons.includes(e))) {
    // Ni à moitié (une pièce jointe de n'importe quel type), ni hors des mails qui valent pièce.
    if (jetons.some((j) => pieceJointe.includes(j))) return false
    const expediteurs = jetons.filter((j) => j.startsWith('from:')).map((j) => j.slice(5))
    if (!expediteursQuiValentPiece(expediteurs, couverture)) return false
    if (!criteresDe(q).some((a) => a.length > 0 && a.every((j) => j.startsWith('from:'))))
      return false
  }
  if (!jetons.some((j) => j.startsWith('after:')) || !jetons.some((j) => j.startsWith('before:')))
    return false
  if (!dedans.every((t) => TERME.test(t) || MONTANT_ECRIT.test(t))) return false
  return criteresDe(q).some((a) => a.length > 0 && a.every(distinctif))
}

// Opérations sans pièce attendue : une liste unique, en données, reprise par la validation des
// opérations chez le cabinet (nature récurrente sans pièce). Lue sur le libellé normalisé ;
// jamais pour un paiement par carte, qui a toujours sa facture.

export type LibelleSansPiece = {
  categorie: CategorieSansPiece
  motif: string
  /** Sur le libellé normalisé (majuscules, sans accents). */
  expressions: readonly RegExp[]
}

export const LIBELLES_SANS_PIECE: readonly LibelleSansPiece[] = [
  {
    categorie: 'frais_bancaires',
    motif: 'Frais bancaires : pas de pièce attendue.',
    expressions: [
      /^(FRAIS|COTIS|COTISATION|COMMISSION|COMMISSIONS|AGIOS|TENUE DE COMPTE|INTERETS DEBITEURS)\b/,
    ],
  },
  {
    categorie: 'assurance_pret',
    motif: 'Assurance de prêt : pas de pièce attendue.',
    expressions: [/\bCAAE\b/, /\bASSURANCES? (DE |DU |D |DES )?(PRET|EMPRUNTEURS?)\b/],
  },
  {
    categorie: 'echeance_pret',
    motif: 'Échéance de prêt : pas de pièce attendue.',
    expressions: [/\bREMBOURSEMENT (DE |DU |D )?PRET\b/, /\bECHEANCE (DE |DU |D )?PRET\b/],
  },
  {
    categorie: 'taxe_fonciere',
    motif: 'Taxe foncière mensualisée : pas de pièce attendue.',
    expressions: [/\bDGFIP\b.*\b(IMPOT TF|TAXE FONCIERE)\b/],
  },
]

/** Une pièce est-elle attendue pour cette opération ? Sinon, sa catégorie et le motif. */
export function pieceAttendue(
  op: Pick<OperationBancaire, 'libelle' | 'nature'>,
): { attendue: true } | { attendue: false; categorie: CategorieSansPiece; motif: string } {
  if (op.nature === 'carte') return { attendue: true }
  const libelle = normaliserLibelle(op.libelle)
  const l = LIBELLES_SANS_PIECE.find((x) => x.expressions.some((e) => e.test(libelle)))
  return l ? { attendue: false, categorie: l.categorie, motif: l.motif } : { attendue: true }
}

// Retenue des mails trouvés : la recherche ne fait entrer que des pièces. Ce qu'elle laisse
// reste dans Gmail, sans rien écrire (ni mail, ni pièce, ni tâche).

export type RetenueRecherche = Extract<IssueMailRecherche, `laisse_${string}`>

/**
 * Avant de lire le mail gardé par le tri : un doute (aucune entité reconnue), un mail classé
 * de l'autre côté de la cloison d'un espace privé (d'une boîte commune vers un espace, ou d'une
 * boîte d'espace vers le commun : jamais par une recherche), ou une demande, reste dans Gmail.
 * La pièce d'une entité de la même cloison est lue, même peu sûre, même d'une autre entité que
 * la cible (elle va où le tri la met).
 */
export function retenueAvantLecture(
  d: Extract<DecisionMail, { garder: true }>,
  cloison: { boite: Cloison; privees: ReadonlySet<string> | readonly string[] },
): RetenueRecherche | null {
  if (d.entiteId === null) return 'laisse_doute'
  if (cloisonDe(d.entiteId, cloison.privees) !== cloison.boite) return 'laisse_autre_cloison'
  if (d.nature === 'demande') return 'laisse_demande'
  return null
}

/** Après lecture : sans pièce jointe PDF ni XML, rien n'entre. */
export function retenueApresLecture(piecesJointes: number): RetenueRecherche | null {
  return piecesJointes > 0 ? null : 'laisse_sans_piece'
}

// Contexte de la recherche (décision du 12/10/2026) : le tri d'une recherche sait quelle entité
// il cherche et par quelle requête le mail est venu. Le classement le reçoit comme une donnée de
// l'appli, à côté du mail et jamais comme une consigne ; un doute rendu par un palier fort est
// proposé sur l'entité cherchée, à vérifier par un humain, jamais rattaché seul.

/**
 * Contexte d'une recherche, posé par le service pour chaque requête : l'entité cherchée, le
 * palier de la requête qui a rendu le mail, et ce qu'elle cherchait. Jamais rien d'un mail.
 */
export type ContexteRecherche = {
  entiteId: string
  /** Nom de l'entité, tel que le classement le lit dans son référentiel. */
  entite: string
  palier: PalierRecherche
  /** Jour de l'opération ; null pour une cible périodique. */
  date: string | null
  /** Période d'une cible périodique ; null pour une opération. */
  periode: FenetreRecherche | null
  /** Montant de l'opération en centimes (son signe est ignoré), avec sa devise. */
  montantCts: number | null
  devise: string | null
  /** Références cherchées (numéro de facture lu dans le libellé). */
  references: readonly string[]
  /**
   * Une personne qui n'est pas administratrice lit l'entité cherchée (une associée, un accès
   * temporaire) : un doute n'y est jamais proposé (sans entité, il n'est visible que des
   * administrateurs). Lu par le service, d'après le périmètre de chaque personne.
   */
  lueHorsAdministrateurs: boolean
}

/** Ce que le classement lit du contexte : rien d'interne, seulement les champs qui ont une valeur. */
export type ContexteRechercheAClasser = {
  entite: string
  palier: PalierRecherche
  date?: string
  periode?: FenetreRecherche
  /** « 1270,00 EUR ». */
  montant?: string
  references?: readonly string[]
}

export function contexteRechercheAClasser(c: ContexteRecherche): ContexteRechercheAClasser {
  const montant = (cts: number, devise: string) => {
    const absolu = Math.abs(cts)
    return `${Math.floor(absolu / 100)},${String(absolu % 100).padStart(2, '0')} ${devise}`
  }
  return {
    entite: c.entite,
    palier: c.palier,
    ...(c.date && { date: c.date }),
    ...(c.periode && { periode: { du: c.periode.du, au: c.periode.au } }),
    ...(c.montantCts !== null &&
      Number.isSafeInteger(c.montantCts) && {
        montant: montant(c.montantCts, c.devise ?? DEVISE_PAR_DEFAUT),
      }),
    ...(c.references.length > 0 && { references: [...c.references] }),
  }
}

export function palierFort(p: PalierRecherche): boolean {
  return (PALIERS_FORTS as readonly PalierRecherche[]).includes(p)
}

/** Pourquoi la pièce d'un doute est à vérifier : la requête qui l'a trouvée, la proposition. */
export function motifPropositionRecherche(palier: PalierRecherche): string {
  const par =
    palier === 'reference'
      ? 'la référence exacte de l’opération'
      : 'l’expéditeur connu du fournisseur'
  return `Trouvée par ${par}, entité non reconnue au tri : proposée sur l’entité cherchée, à confirmer.`
}

/**
 * Un doute (aucune entité reconnue) lu comme une pièce et rendu par un palier fort : gardé comme
 * pièce sur l'entité cherchée, à vérifier, si elle n'est lue que des administrateurs (ce qui n'a
 * pas d'entité n'est visible que d'eux, la proposition n'en sort pas avant qu'un humain l'ait
 * vérifiée), qu'elle n'est pas archivée, qu'elle est de la cloison de la boîte et que la boîte
 * peut y garder un mail (`mailVisibleDuProprietaire`). Sinon null : le doute reste dans Gmail
 * (demande, classement impossible, palier faible, relève sans contexte, cible lue d'une associée,
 * archivée, autre cloison). Une décision qui a une entité garde la sienne.
 */
export function propositionDoute(
  d: Extract<DecisionMail, { garder: true }>,
  e: {
    contexte: Pick<ContexteRecherche, 'entiteId' | 'palier' | 'lueHorsAdministrateurs'> | null
    cloison: { boite: Cloison; privees: ReadonlySet<string> | readonly string[] }
    /** Périmètre du tri de la boîte. */
    perimetre: Perimetre
    /** Les entités que le tri connaît : une cible archivée, ou qu'il ne connaît pas, jamais. */
    entites: readonly { id: string; archivee: boolean }[]
  },
): Extract<DecisionMail, { garder: true }> | null {
  const c = e.contexte
  if (d.entiteId !== null || d.nature !== 'piece' || !c || !palierFort(c.palier)) return null
  if (c.lueHorsAdministrateurs) return null
  const cible = e.entites.find((x) => x.id === c.entiteId)
  if (!cible || cible.archivee) return null
  if (cloisonDe(c.entiteId, e.cloison.privees) !== e.cloison.boite) return null
  if (!mailVisibleDuProprietaire(e.perimetre, c.entiteId)) return null
  return {
    garder: true,
    entiteId: c.entiteId,
    nature: 'piece',
    aVerifier: [...d.aVerifier, motifPropositionRecherche(c.palier)],
  }
}

// Budgets (décision du 12/10/2026) : la recherche ne prend que 70 % du plafond IA mensuel de la
// cloison ; 0,10 USD par opération, 3 USD par lot (10 USD au plus, sur demande).

export const PART_PLAFOND_RECHERCHE = 0.7
/** Dépense IA de la recherche, en micro-USD. */
export const BUDGETS_RECHERCHE_MICRO_USD = {
  operation: 100_000,
  periodique: 300_000,
  lot: 3_000_000,
  lotMax: 10_000_000,
} as const
/** Mails demandés à Gmail par requête : au-delà, la requête est trop large. */
export const MAILS_MAX_PAR_PALIER = { operation: 15, periodique: 60 } as const
/** Mails nouveaux classés pour une opération ou une cible, au plus. */
export const MAILS_CLASSES_MAX = { operation: 12, periodique: 40 } as const
export const OPERATIONS_MAX_LOT = 300
export const CIBLES_MAX_LOT = 50
/** Période d'une cible périodique, au plus. */
export const PERIODE_MAX_JOURS = 400

/**
 * Lecture estimée d'une pièce entrée par une recherche (lecture par Claude, placement dans le
 * plan), en micro-USD : faite ensuite par le worker, elle compte dès l'entrée dans les budgets
 * de la recherche et dans sa part du plafond IA.
 */
export const LECTURE_PIECE_ESTIMEE_MICRO_USD = 50_000

/** La part du plafond IA mensuel (micro-USD) ouverte à la recherche. */
export function plafondPourRecherche(plafondMicroUsd: number): number {
  return Math.floor(plafondMicroUsd * PART_PLAFOND_RECHERCHE)
}

// Entrées (Zod). Les natures d'opération viennent du type de operations-bancaires.ts ; la liste
// NATURES_OPERATION de domaine.ts les remplacera quand elle y sera.

const NATURES = {
  carte: 'carte',
  prelevement: 'prelevement',
  virement: 'virement',
  autre: 'autre',
} as const satisfies { [N in NatureOperation]: N }

const jourCivil = z.iso.date()
const MONTANT_MAX_CTS = 10_000_000_000

/** Référence d'une opération chez sa source (« dougs:4521 ») : clé du résultat et du journal. */
export const refOperation = z
  .string()
  .trim()
  .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,119}$/, 'Référence : lettres, chiffres, . _ : -')

/** Adresse ou domaine d'expéditeur, sans caractère qui sortirait de from:. */
export const adresseRecherche = motifExpediteur.regex(
  ADRESSE,
  'Adresse ou domaine : factures@exemple.fr ou exemple.fr',
)

/** Un mot cherché tel quel : ni espace, ni guillemet, ni parenthèse, ni deux-points. */
export const motRecherche = z
  .string()
  .trim()
  .regex(/^[\p{L}\p{N}][\p{L}\p{N}.'&_-]{2,39}$/u, 'Un mot de trois signes au moins, sans espace')

/** Une opération bancaire, quelle que soit sa source. */
export const operationBancaireEntree = z
  .object({
    entiteId: z.uuid(),
    date: jourCivil,
    montantCts: z
      .number()
      .int()
      .min(-MONTANT_MAX_CTS)
      .max(MONTANT_MAX_CTS)
      .refine((m) => m !== 0, 'Montant nul'),
    devise: z
      .string()
      .regex(/^[A-Z]{3}$/)
      .default(DEVISE_PAR_DEFAUT),
    libelle: z.string().trim().min(1).max(300),
    marchand: z.string().trim().min(1).max(200).nullable().default(null),
    nature: z.enum(NATURES).default('autre'),
  })
  .strict()

const periodeValide = (c: FenetreRecherche, ctx: z.RefinementCtx) => {
  if (c.du > c.au) ctx.addIssue({ code: 'custom', path: ['au'], message: 'Fin avant le début.' })
  else if (ecartJours(c.du, c.au) > PERIODE_MAX_JOURS)
    ctx.addIssue({
      code: 'custom',
      path: ['au'],
      message: `Période de ${PERIODE_MAX_JOURS} jours au plus.`,
    })
}

/** Cible périodique : les pièces d'un expéditeur ou de quelques mots sur une période. */
export const ciblePeriodique = z
  .object({
    ref: refOperation.optional(),
    entiteId: z.uuid(),
    expediteurs: z.array(adresseRecherche).max(10).default([]),
    mots: z.array(motRecherche).max(5).default([]),
    du: jourCivil,
    au: jourCivil,
  })
  .strict()
  .superRefine((c, ctx) => {
    if (c.expediteurs.length === 0 && c.mots.length === 0)
      ctx.addIssue({
        code: 'custom',
        path: ['expediteurs'],
        message: 'Au moins un expéditeur ou un mot.',
      })
    periodeValide(c, ctx)
  })
export type CiblePeriodique = z.output<typeof ciblePeriodique>

const LONGUEUR_MAX_REF = 120

/** Empreinte courte et stable d'un texte (deux hachages FNV-1a de 32 bits), en hexadécimal. */
function empreinteCourte(texte: string): string {
  let a = 0x811c9dc5
  let b = 0x01000193
  for (let i = 0; i < texte.length; i++) {
    const c = texte.charCodeAt(i)
    a = Math.imul(a ^ c, 0x01000193) >>> 0
    b = Math.imul(b ^ c, 0x9e3779b1) >>> 0
  }
  return a.toString(16).padStart(8, '0') + b.toString(16).padStart(8, '0')
}

/**
 * Ref d'une cible : donnée, sinon son entité, sa période et ses termes triés ; des termes trop
 * longs pour la ref y sont remplacés par leur empreinte (jamais coupés : deux cibles ne
 * partagent pas une ref).
 */
export function refCiblePeriodique(
  c: Pick<CiblePeriodique, 'ref' | 'entiteId' | 'du' | 'au' | 'expediteurs' | 'mots'>,
): string {
  if (c.ref) return c.ref
  const termes = [...c.expediteurs, ...c.mots].sort().join('+')
  const tete = `periodique:${c.entiteId}:${c.du}:${c.au}:`
  const ref = tete + termes
  return ref.length <= LONGUEUR_MAX_REF ? ref : `${tete}#${empreinteCourte(termes)}`
}

/**
 * Lot de recherche (script de rattrapage) : les opérations d'une entité, chacune avec sa ref
 * (son entité, si elle est donnée, est celle du lot), des cibles périodiques, l'essai toujours
 * explicite, et le budget IA du lot.
 */
export const demandeRecherchePieces = z
  .object({
    entiteId: z.uuid(),
    operations: z
      .array(
        z
          .object({
            ref: refOperation,
            operation: operationBancaireEntree.extend({ entiteId: z.uuid().optional() }),
          })
          .strict(),
      )
      .max(OPERATIONS_MAX_LOT)
      .default([]),
    periodiques: z.array(ciblePeriodique).max(CIBLES_MAX_LOT).default([]),
    essai: z.boolean(),
    budgetMicroUsd: z
      .number()
      .int()
      .positive()
      .max(BUDGETS_RECHERCHE_MICRO_USD.lotMax)
      .default(BUDGETS_RECHERCHE_MICRO_USD.lot),
  })
  .strict()
  .superRefine((d, ctx) => {
    if (d.operations.length === 0 && d.periodiques.length === 0)
      ctx.addIssue({ code: 'custom', path: ['operations'], message: 'Rien à chercher.' })
    d.operations.forEach((o, i) => {
      if (o.operation.entiteId !== undefined && o.operation.entiteId !== d.entiteId)
        ctx.addIssue({
          code: 'custom',
          path: ['operations', i, 'operation', 'entiteId'],
          message: 'Opération d’une autre entité que le lot.',
        })
    })
    const refs = [...d.operations.map((o) => o.ref), ...d.periodiques.map(refCiblePeriodique)]
    const doubles = refs.filter((r, i) => refs.indexOf(r) !== i)
    if (doubles.length)
      ctx.addIssue({
        code: 'custom',
        path: ['operations'],
        message: `Refs en double : ${[...new Set(doubles)].join(', ')}.`,
      })
  })
export type DemandeRecherchePieces = z.output<typeof demandeRecherchePieces>

/** Les opérations du lot, sur son entité, avec leur ref : l'entrée du port. */
export function operationsDuLot(
  d: Pick<DemandeRecherchePieces, 'entiteId' | 'operations'>,
): (OperationBancaire & { ref: string })[] {
  return d.operations.map(({ ref, operation }) => ({ ...operation, entiteId: d.entiteId, ref }))
}

// Bilan d'une recherche : l'issue du port et son motif, du plus précis au plus général. Ni
// adresse, ni objet, ni rien du contenu d'un mail.

export type CompteRecherche = {
  /** Boîtes interrogeables qui couvrent l'entité. */
  boites: number
  /** Requêtes possibles (0 : aucun critère sûr). */
  requetes: number
  /** Pièces de la cible venues d'un mail de la fenêtre, entrées ou déjà connues. */
  pieceIds: readonly string[]
  mailsLus: number
  coupure: CoupureRecherche | null
  /** Motif de pieceAttendue quand aucune pièce n'est attendue. */
  sansPiece?: string | null
  /** Fenêtre vide : ces jours-là sont à la relève. */
  fenetreVide?: boolean
  /** Pièces trouvées, classées sur une autre entité de la même cloison. */
  autreEntite?: number
  /** Pièces trouvées, encore sans entité (à classer). */
  sansEntite?: number
  /** Mails trouvés déjà relevés, écartés sans être relus. */
  connus?: number
}

const LIBELLES_COUPURE: Record<CoupureRecherche, string> = {
  plafond_mails: 'trop de mails',
  plafond_ia: 'part du plafond IA atteinte',
  budget: 'budget épuisé',
  erreur_gmail: 'erreur Gmail',
  erreur_ia: 'erreur du classement IA',
  erreur_interne: 'erreur interne',
}

const MOTIFS_COUPURE: Record<CoupureRecherche, string> = {
  plafond_mails: 'Trop de mails trouvés : préciser le tiers ou ses adresses.',
  plafond_ia: 'Part du plafond IA du mois atteinte : à reprendre.',
  budget: 'Budget de la recherche épuisé : à reprendre.',
  erreur_gmail: 'Erreur Gmail : à reprendre.',
  erreur_ia: 'Erreur du classement IA : à reprendre.',
  erreur_interne: 'Erreur interne pendant le tri : à reprendre.',
}

/**
 * Résultat du port pour une opération ou une cible : 'trouvee' dès qu'une pièce vient d'un mail
 * de la fenêtre ; sinon pas de pièce attendue ou fenêtre vide ('rien'), aucune boîte
 * ('sans_boite'), coupure ('coupee'), puis 'rien' avec le motif le plus précis.
 */
export function resultatRecherche(c: CompteRecherche): ResultatRechercheMails {
  const pieceIds = [...new Set(c.pieceIds)]
  const res = (issue: IssueRechercheMails, motif?: string): ResultatRechercheMails => ({
    issue,
    pieceIds,
    mailsLus: c.mailsLus,
    ...(motif ? { motif } : {}),
  })
  if (pieceIds.length)
    return res(
      'trouvee',
      c.coupure
        ? `Recherche coupée (${LIBELLES_COUPURE[c.coupure]}) : d’autres pièces sont possibles.`
        : undefined,
    )
  if (c.sansPiece) return res('rien', c.sansPiece)
  if (c.fenetreVide) return res('rien', 'Fenêtre vide : les mails de ces jours sont à la relève.')
  if (c.boites === 0) return res('sans_boite', 'Aucune boîte active ne couvre l’entité.')
  if (c.coupure) return res('coupee', MOTIFS_COUPURE[c.coupure])
  if (c.requetes === 0) return res('rien', 'Aucun critère sûr : donner le tiers ou ses adresses.')
  if (c.sansEntite) return res('rien', 'Pièce trouvée, pas encore classée : la classer.')
  if (c.autreEntite) return res('rien', 'Mails trouvés, classés sur une autre entité.')
  if (c.mailsLus === 0 && c.connus)
    return res('rien', 'Mails déjà relevés, sans pièce de l’entité.')
  if (c.mailsLus === 0) return res('rien', 'Aucun mail avec un PDF ou un XML dans la fenêtre.')
  return res('rien', 'Mails lus, aucune pièce gardée.')
}
